import { AlertTriangle, HelpCircle, Trash2, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface ConfirmOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'warning' | 'neutral';
}

type ConfirmAction = (options: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<ConfirmAction | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((confirmed: boolean) => void) | null>(null);
  const cancelButton = useRef<HTMLButtonElement | null>(null);

  const finish = useCallback((confirmed: boolean) => {
    resolver.current?.(confirmed);
    resolver.current = null;
    setOptions(null);
  }, []);

  const confirm = useCallback<ConfirmAction>((nextOptions) => {
    resolver.current?.(false);
    setOptions(nextOptions);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  useEffect(() => {
    if (!options) return;
    cancelButton.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [options, finish]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <div
          className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) finish(false);
          }}
        >
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="atlas-confirm-title"
            aria-describedby="atlas-confirm-description"
            className="atlas-enter w-full max-w-md rounded-2xl border border-atlas-border bg-atlas-elevated p-6 shadow-atlas-float"
          >
            <div className="flex items-start justify-between gap-4">
              <div
                className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${options.tone === 'neutral' ? 'bg-atlas-subtle text-atlas-ink' : options.tone === 'warning' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'}`}
              >
                {options.tone === 'neutral' ? (
                  <HelpCircle className="h-5 w-5" />
                ) : options.tone === 'warning' ? (
                  <AlertTriangle className="h-5 w-5" />
                ) : (
                  <Trash2 className="h-5 w-5" />
                )}
              </div>
              <button
                onClick={() => finish(false)}
                className="grid h-8 w-8 place-items-center rounded-lg text-atlas-muted hover:bg-atlas-subtle"
                aria-label="Close confirmation"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <h2 id="atlas-confirm-title" className="mt-4 text-lg font-semibold text-atlas-ink">
              {options.title}
            </h2>
            <p id="atlas-confirm-description" className="mt-2 text-sm leading-6 text-atlas-muted">
              {options.description}
            </p>
            <div className="mt-6 flex justify-end gap-2 border-t border-atlas-border pt-4">
              <button
                ref={cancelButton}
                onClick={() => finish(false)}
                className="atlas-button-secondary"
              >
                {options.cancelLabel || 'Cancel'}
              </button>
              <button
                onClick={() => finish(true)}
                className={options.tone === 'neutral' ? 'atlas-button-primary' : `h-10 rounded-lg px-4 text-sm font-semibold text-white ${options.tone === 'warning' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {options.confirmLabel || 'Delete'}
              </button>
            </div>
          </section>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used inside ConfirmProvider');
  return confirm;
}
